const Wishlist = {

  STORAGE_KEY: 'shopify_wishlist',

  get() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  save(list) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
  },

  has(handle) {
    return this.get().includes(String(handle));
  },

  toggle(handle) {
    handle = String(handle);
    let list = this.get();
    if (list.includes(handle)) {
      list = list.filter(item => item !== handle);
    } else {
      list.push(handle);
    }
    this.save(list);
    this.updateAllButtons();
    this.updateHeaderCount();
    return list;
  },

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const formatted = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    try {
      const sym = Shopify.currency.active || 'EUR';
      const map = { EUR: '€', GBP: '£', USD: '$', CNY: '¥', INR: '₹' };
      return (map[sym] || sym + ' ') + formatted;
    } catch(e) {
      return '€' + formatted;
    }
  },

  updateAllButtons() {
    const list = this.get();
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      const active = list.includes(handle);
      btn.classList.toggle('wishlist-active', active);
      const icon = btn.querySelector('svg');
      if (icon) {
        icon.style.fill = active ? 'red' : 'none';
        icon.style.stroke = active ? 'red' : 'currentColor';
      }
    });
  },

  updateHeaderCount() {
    const count = this.get().length;
    document.querySelectorAll('.wishlist-header-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  },

  async addToCart(variantId, btn, handle) {
    btn.disabled = true;
    btn.style.opacity = '0.6';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      await res.json();

      // Remove product from wishlist after successful add to cart
      if (handle) {
        this.removeFromPage(handle);
      }

      // Fire common Shopify theme cart drawer events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('theme:cart:open'));

      // Fallback: directly click the header cart icon to open the drawer
      const cartTrigger =
        document.querySelector('[data-cart-toggle]') ||
        document.querySelector('[data-drawer-toggle="cart-drawer"]') ||
        document.querySelector('[data-cart-drawer-toggle]') ||
        document.querySelector('[aria-controls="cart-drawer"]') ||
        document.querySelector('a[href="/cart"].header__icon') ||
        document.querySelector('.cart-count-bubble')?.closest('a, button');

      if (cartTrigger) cartTrigger.click();

    } catch(e) {
      // silent fail
    } finally {
      btn.disabled = false;
      btn.style.opacity = '1';
    }
  },

  async renderPage() {
    const loading = document.getElementById('wishlist-loading');
    const grid = document.getElementById('wishlist-grid');
    const empty = document.getElementById('wishlist-empty');
    const countText = document.getElementById('wishlist-count-text');

    if (!grid) return;

    const list = this.get();

    if (list.length === 0) {
      if (loading) loading.style.display = 'none';
      if (empty) empty.style.display = 'block';
      return;
    }

    const results = await Promise.all(
      list.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(r => r.json())
          .catch(() => null)
      )
    );

    const products = results.filter(Boolean);

    if (loading) loading.style.display = 'none';

    if (products.length === 0) {
      if (empty) empty.style.display = 'block';
      return;
    }

    if (countText) {
      countText.textContent = `${products.length} item${products.length !== 1 ? 's' : ''}`;
    }

    grid.innerHTML = products.map(p => {
      const image = p.featured_image || '';
      const available = p.variants && p.variants.some(v => v.available);
      const firstVariantId = p.variants && p.variants[0] ? p.variants[0].id : null;
      const price = this.formatMoney(p.price);
      const hasCompare = p.compare_at_price && p.compare_at_price > p.price;
      const comparePriceHtml = hasCompare
        ? `<span class="wishlist-card__compare">${this.formatMoney(p.compare_at_price)}</span>`
        : '';
      const priceClass = hasCompare ? 'wishlist-card__sale' : '';
      const availabilityHtml = available
        ? `<span class="wishlist-card__availability in-stock">In Stock</span>`
        : `<span class="wishlist-card__availability">Sold Out</span>`;

      // Cart button — matches header bag icon
      const cartBtn = available && firstVariantId ? `
        <button
          class="wishlist-card__btn-cart"
          onclick="Wishlist.addToCart(${firstVariantId}, this, '${p.handle}')"
          title="Add to Cart"
          aria-label="Add to Cart"
        >
          <svg aria-hidden="true" fill="none" focusable="false" width="16" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 10h20l-4 11H6L2 10Zm14-3a4 4 0 0 0-8 0"/>
          </svg>
        </button>` : '';

      return `
        <div class="wishlist-card" data-wishlist-card="${p.handle}">
          <div class="wishlist-card__image-wrap">
            <a href="/products/${p.handle}">
              <img src="${image}" alt="${p.title}" loading="lazy">
            </a>

            <!-- Heart button: top right -->
            <button
              class="wishlist-card__btn-heart"
              onclick="Wishlist.removeFromPage('${p.handle}')"
              title="Remove from Wishlist"
              aria-label="Remove from Wishlist"
            >
              <svg viewBox="0 0 24 24" fill="red" stroke="red" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>

            <!-- Cart button: bottom right -->
            ${cartBtn}
          </div>
          <a href="/products/${p.handle}" class="wishlist-card__title">${p.title}</a>
          <div class="wishlist-card__price">
            ${comparePriceHtml}<span class="${priceClass}">${price}</span>
          </div>
          ${availabilityHtml}
        </div>
      `;
    }).join('');

    grid.style.display = 'grid';
  },

  removeFromPage(handle) {
    this.toggle(handle);
    const card = document.querySelector(`[data-wishlist-card="${handle}"]`);
    if (card) card.remove();
    const remaining = this.get().length;
    const countText = document.getElementById('wishlist-count-text');
    if (countText) {
      countText.textContent = remaining > 0
        ? `${remaining} item${remaining !== 1 ? 's' : ''}`
        : '';
    }
    if (remaining === 0) {
      document.getElementById('wishlist-grid').style.display = 'none';
      document.getElementById('wishlist-empty').style.display = 'block';
    }
    this.updateHeaderCount();
  },

  init() {
    this.updateAllButtons();
    this.updateHeaderCount();

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.productHandle;
      this.toggle(handle);
    });

    if (document.getElementById('wishlist-grid')) {
      this.renderPage();
    }
  }

};

document.addEventListener('DOMContentLoaded', () => {
  Wishlist.init();
});